import moment from "moment";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { repeatOptions, taskDueDates, taskDueDatesValues } from "../utils/globalConstants";
import ErrorText from "./errorText";
import * as yup from "yup";
import update from "immutability-helper";
import { DatePicker, TimePicker } from "antd";
import {
  createNewTask,
  updateTaskbyId,
  getUserOrganizationList,
} from "../services/taskService";
import toast, { Toaster } from "react-hot-toast";
import { setLeadTaskList } from "../actions/actions";
import { Logger } from '../utils/logger';
import styled from "styled-components";
import { convertTimeToIST, convertToIST } from "../utils/helpers";

interface taskTypeI {
  type: string;
  toBePerformAt: string | undefined;
  dueDate: string;
  time: string;
  repeat: string;
  assignedTo: string;
  notes: string;
  leadIds: Array<string>;
}
interface Props {
  leadIds: Array<{ id: string }> | string[];
  isOpen?: boolean;
  onClose?: () => void;
  modalId?: string;
  updateModalValue?: any;
  action: string;
  formName?: string;
  onTaskDelete?: (id: string) => void;
  drawerClose?: () => void;
  fetchTaskStatusFollowUps?: (data?: any) => void;
  filterParams?: any; // NEW: For bulk task with filter selection mode
  useFilter?: boolean; // NEW: Flag to use filterParams instead of leadIds
}

const Root = styled.div`
  .form_label {
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
    font-size: 14px;
  }

  .form-select, .form-control {
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 14px;
    
    &:focus {
      outline: none;
      border-color: #6366f1;
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
    }
  }

  .note_area {
    resize: vertical;
    min-height: 80px;
  }

  .timepicker-element {
    .ant-picker {
      width: 100%;
      border-radius: 6px;
    }
  }

  .date-picker-container {
    .ant-picker {
      width: 100%;
      border-radius: 6px;
    }
  }
`;

export const DUE_DATE_FORMAT = "YYYY-MM-DDTHH:mm:ss.SSSZ";

const CreateLeadDetailsTaskForm: React.FC<PropsWithChildren<Props>> = ({
  leadIds,
  modalId,
  updateModalValue,
  action,
  formName = "Task",
  onTaskDelete,
  isOpen,
  onClose,
  drawerClose,
  fetchTaskStatusFollowUps,
  filterParams, // NEW: For bulk task with filter selection mode
  useFilter = false, // NEW: Flag to use filterParams instead of leadIds
}) => {
  const [isCustomDate, toggleCustomDate] = useState(false);
  const [taskList, setTaskList] = useState<Array<any>>([]);
  const [taskAssignTo, setTaskAssignTo] = useState<Array<any>>([]);
  const [isCompleted, setIsCompleted] = useState(false);

  // Convert leadIds to proper array format
  const getLeadIdsArray = useCallback(() => {
    if (!leadIds) return [];
    
    // Handle bulk selection case (array of strings)
    if (Array.isArray(leadIds) && leadIds.length > 0 && typeof leadIds[0] === 'string') {
      return leadIds as string[];
    }
    
    // Handle lead details case (array of objects with id property)
    if (Array.isArray(leadIds) && leadIds.length > 0 && typeof leadIds[0] === 'object') {
      return (leadIds as Array<{ id: string }>).map(lead => lead.id);
    }
    
    return [];
  }, [leadIds]);

  const [taskData, setTaskData] = useState<taskTypeI>({
    type: "",
    toBePerformAt: moment().format('YYYY-MM-DDTHH:mm:ss'),
    dueDate: "",
    time: moment().format('YYYY-MM-DDTHH:mm:ss'),
    repeat: "NoRepeat",
    assignedTo: "",
    notes: "",
    leadIds: getLeadIdsArray(),
  });

  const [errors, setErrors] = useState({
    type: "",
    toBePerformAt: "",
    time: "",
    // repeat: "",
    // assignedTo: "",
    notes: "",
  });
  const dispatch = useDispatch();
  const StoreData = useSelector((state: any) => {
    return state?.rootReducers;
  });

  const handleSetDueDate = useCallback((dueDateType: string) => {
    setErrors(prev => ({
      ...prev,
      toBePerformAt: "",
    }));

    switch (dueDateType) {
      case "today":
      case "tomorrow":
      case "3d":
      case "1w":
      case "1m":
        const dt = moment()
          .add(taskDueDatesValues[dueDateType as keyof typeof taskDueDatesValues], "days")
          .format("YYYY-MM-DD");
          
        // Preserve existing time if it exists
        const existingTime = taskData.time 
          ? moment(taskData.time).format("HH:mm:ss")
          : moment().format("HH:mm:ss");
        
        const combined = moment(dt + "T" + existingTime);
        
        setTaskData(prev => ({
          ...prev,
          toBePerformAt: combined.toISOString(),
          dueDate: dueDateType,
          time: combined.toISOString()
        }));
        toggleCustomDate(false);
        break;

      case "custom":
        // Preserve current date/time when switching to custom
        const currentDateTime = taskData.toBePerformAt 
          ? moment(taskData.toBePerformAt)
          : moment();
          
        setTaskData(prev => ({
          ...prev,
          toBePerformAt: currentDateTime.toISOString(),
          dueDate: dueDateType,
          time: currentDateTime.toISOString()
        }));
        toggleCustomDate(true);
        break;

      default:
        break;
    }
  }, [taskData.time, taskData.toBePerformAt]);

  useEffect(() => {
    handleGetUserOrganization();
    setTaskList(StoreData?.user?.userPreferences?.taskType);
  }, []);

  useEffect(() => {
    // Update leadIds when prop changes
    const newLeadIds = getLeadIdsArray();
    if (newLeadIds.length > 0) {
      setTaskData(prev => ({
        ...prev,
        leadIds: newLeadIds
      }));
    }
  }, [getLeadIdsArray]);

  useEffect(() => {
    if (action === "edit" || action === "Edit") {
      if (updateModalValue && Object.values(updateModalValue).length > 0) {
        Logger.log('Original UTC time:', updateModalValue.toBePerformAt);
        
        // Explicitly handle IST conversion (UTC+5:30)
        const utcTime = moment.utc(updateModalValue.toBePerformAt);
        const istOffset = 330; // IST is UTC+5:30 = 330 minutes
        const localTime = utcTime.utcOffset(istOffset);
        
        Logger.log('Conversion steps:', {
          originalUTC: utcTime.format(),
          afterISTConversion: localTime.format(),
          displayTime: localTime.format('hh:mm A')
        });

        setTaskData({
          type: updateModalValue.type,
          toBePerformAt: localTime.format('YYYY-MM-DDTHH:mm:ss'),
          dueDate: "custom",
          time: localTime.format('YYYY-MM-DDTHH:mm:ss'),
          repeat: updateModalValue.repeat || "NoRepeat",
          assignedTo: updateModalValue?.assignedTo[0]?._id,
          notes: updateModalValue.notes || "",
          leadIds: getLeadIdsArray(),
        });

        Logger.log('Set form data with IST time:', {
          toBePerformAt: localTime.format('YYYY-MM-DDTHH:mm:ss'),
          formattedDisplayTime: localTime.format('hh:mm A')
        });

        toggleCustomDate(true);
        setIsCompleted(updateModalValue?.isCompleted ?? false);
      }
    }
  }, [updateModalValue, action, getLeadIdsArray]);

  const handleGetUserOrganization = useCallback(async () => {
    // First try to get from localStorage (same as CreateTaskForm)
    const data = localStorage.getItem("userData");
    if (data) {
      let tempArray = [...JSON.parse(data).organizationEmployee];
      tempArray.forEach((el: any) => {
        let findRole = StoreData.userData.userDetails.organizationRoles.find(
          (x: any) => x._id == el.role
        ).displayName;
        el.roleName = findRole;
        el.organizationName = StoreData.userData.userDetails.organization.name;
      });

      setTaskAssignTo(tempArray);
      return;
    }

    // Fallback to API call
    try {
      const response = await getUserOrganizationList({
        page: 1,
        perPage: 15,
      });
      if (response && response.status) {
        let tempArray = [...response.data.data];
        tempArray.forEach((el: any) => {
          let findRole = StoreData.userData.userDetails.organizationRoles.find(
            (x: any) => x._id == el.role
          ).displayName;
          el.roleName = findRole;
          el.organizationName = StoreData.userData.userDetails.organization.name;
        });

        setTaskAssignTo(tempArray);
      }
    } catch (err) {
      Logger.log(err, "Error");
    }
  }, [StoreData.userData.userDetails]);

  // Add modern date/time handlers
  const handleDateChange = useCallback((
    name: string,
    value: moment.Moment | null, 
    type = ""
  ) => {
    Logger.log('handleDateChange entry:', {
      type,
      value: value?.format(),
      currentTime: moment(taskData.toBePerformAt).format('HH:mm'),
      fullState: taskData
    });

    if (type === "time") {
      // Time picker logic
      const newTime = moment(value).format("HH:mm"); 
      const currentDate = moment(taskData.toBePerformAt).format("YYYY-MM-DD");
      const combined = moment(`${currentDate} ${newTime}`, "YYYY-MM-DD HH:mm");

      Logger.log('Time change:', {
        newTime,
        currentDate, 
        combined: combined.format()
      });

      setTaskData(prev => ({
        ...prev,
        toBePerformAt: combined.toISOString(),
        time: combined.toISOString()
      }));

    } else {
      // Date picker logic
      const newDate = moment(value).format("YYYY-MM-DD");
      const currentTime = taskData.time 
        ? moment(taskData.time).format("HH:mm")
        : moment().format("HH:mm");
      const combined = moment(`${newDate} ${currentTime}`, "YYYY-MM-DD HH:mm");

      Logger.log('Date change:', {
        newDate,
        currentTime,
        combined: combined.format()
      });

      setTaskData(prev => ({
        ...prev,
        [name]: combined.toISOString(),
        time: combined.toISOString()
      }));
    }

    setErrors(prev => ({
      ...prev,
      [name]: "",
      time: ""
    }));
  }, [taskData]);

  let schema = yup.object().shape({
    type: yup.string().required("Task Type is required"),
    toBePerformAt: yup.string().required("Due Date is required"),
    // repeat: yup.string().required("Repeat is required"),
    time: yup.string().required("Time is required"),
    // assignedTo: yup.string().required("Task Assigned is required"),
    notes: yup.string().max(200, "Max 200 characters are allowed"),
  });

  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.currentTarget;
    setTaskData(prev => ({
      ...prev,
      [name]: value,
    }));
    setErrors(prev => ({
      ...prev,
      [name]: "",
    }));
  }, []);

  const handleTaskSubmit = useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    
    const isFormValid = await schema.isValid(taskData, {
      abortEarly: false,
    });

    if (isFormValid) {
      let assignData = [];
      if (taskData.assignedTo?.length > 0) {
        assignData.push(taskData.assignedTo);
      }

      let tempObj: any = {};
      if (action === "Add" || action === "add") {
        tempObj = {
          // NEW: Use filterParams when in filter selection mode, otherwise use leadIds
          ...(useFilter && filterParams
            ? { filterParams: filterParams }
            : { leadIds: taskData.leadIds }
          ),
          type: taskData.type,
          toBePerformAt: taskData.toBePerformAt,
          extraDetails: {},
          ...(assignData?.length > 0 && {
            assignedTo: assignData,
          }),
          ...(taskData.repeat !== "" && taskData.repeat !== "NoRepeat" && {
            repeat: taskData.repeat,
          }),
          ...(taskData.notes?.length > 0 && {
            notes: taskData.notes,
          }),
          isCompleted: isCompleted,
        };

        // Log for debugging
        if (useFilter) {
          Logger.log('📋 Creating bulk task with filterParams:', { filterParams, tempObj });
        }
      } else {
        tempObj = {
          ...(taskData.notes?.length > 0 && {
            notes: taskData.notes,
          }),
          type: taskData.type,
          toBePerformAt: taskData.toBePerformAt,
          extraDetails: {},
          ...(assignData?.length > 0 && {
            assignedTo: assignData,
          }),
          ...(taskData.repeat !== "" && taskData.repeat !== "NoRepeat" && {
            repeat: taskData.repeat,
          }),
          isCompleted: isCompleted,
        };
      }

      try {
        let response: any = "";
        if (action === "Add" || action === "add") {
          response = await createNewTask(tempObj);
        } else {
          response = await updateTaskbyId(updateModalValue._id, tempObj);
        }
        
        if (response && response.status) {
          // Call appropriate callbacks
          if (drawerClose) {
            drawerClose();
          } else if (onClose) {
            onClose();
          }
          
          if (fetchTaskStatusFollowUps) {
            fetchTaskStatusFollowUps(response?.data);
          }

          // Update Redux store if in lead details mode
          if (StoreData?.leadTask?.leadTask) {
            const responseData = response?.data;
            const tempArray = [...StoreData?.leadTask?.leadTask];
            if (action === "Add" || action === "add") {
              tempArray?.unshift(responseData?.data);
            } else {
              const tempData = tempArray.findIndex(
                (x) => x._id == responseData.data._id
              );
              tempArray.splice(tempData, 1, responseData.data);
            }
            dispatch(setLeadTaskList(tempArray));
          }

          toast.success(response?.data?.message);
          resetFormvalue();
          
          // Legacy modal hide for backward compatibility
          if (modalId) {
            $(modalId).modal("hide");
          }
        }
      } catch (err) {
        toast.error("Error while creating new task");
      }
    } else {
      schema.validate(taskData, { abortEarly: false }).catch((err) => {
        const errors = err.inner.reduce(
          (acc: any, error: { path: string; message: string }) => {
            return {
              ...acc,
              [error.path]: error.message,
            };
          },
          {}
        );
        setErrors((prevErrors) =>
          update(prevErrors, {
            $set: errors,
          })
        );
      });
    }
  }, [taskData, action, updateModalValue, drawerClose, onClose, fetchTaskStatusFollowUps, StoreData?.leadTask?.leadTask, dispatch, modalId, isCompleted]);
  const resetFormvalue = useCallback(() => {
    setTaskData({
      type: "",
      toBePerformAt: moment().format('YYYY-MM-DDTHH:mm:ss'),
      dueDate: "",
      time: moment().format('YYYY-MM-DDTHH:mm:ss'),
      repeat: "NoRepeat",
      assignedTo: "",
      notes: "",
      leadIds: getLeadIdsArray(),
    });
    setIsCompleted(false);
    toggleCustomDate(false);
    setErrors({
      type: "",
      toBePerformAt: "",
      time: "",
      notes: "",
    });
  }, [getLeadIdsArray]);

  const handleCloseActivityModal = useCallback(() => {
    resetFormvalue();
    if (modalId) {
      $(modalId).modal("hide");
    }
    if (drawerClose) {
      drawerClose();
    } else if (onClose) {
      onClose();
    }
  }, [resetFormvalue, modalId, drawerClose, onClose]);

  // Memoize task assign options
  const taskAssignOptions = useMemo(() => 
    taskAssignTo?.map((item: any, j: number) => (
      <option value={item?._id} key={j}>
        {item.firstName} - {item.roleName} - {item.organizationName}
      </option>
    ))
  , [taskAssignTo]);

  // Memoize task options
  const taskOptions = useMemo(() => 
    taskList?.map((task, j) => (
      <option value={task?.value} key={j}>
        {task?.name}
      </option>
    ))
  , [taskList]);
  return (
    <Root>
      {/* Legacy modal support - conditionally render if modalId is provided */}
      {modalId ? (
        <div
          className="modal fade popup-section-1 popup-section-2 popup-section-3"
          data-backdrop="static"
          data-keyboard="false"
          id="AddNewTask"
          role="dialog"
          tabIndex={-1}
          style={{ zIndex: "999999999999999" }}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{action} Task</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => handleCloseActivityModal()}
                ></button>
              </div>
              <div className="form_container_body">
                {renderFormContent()}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={(e) => handleTaskSubmit(e)}
                  className="btn LeadsFilterApply"
                >
                  SAVE
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Modern component-based rendering for drawer usage
        renderFormContent()
      )}
      <Toaster position="top-right" reverseOrder={false} />
    </Root>
  );

  function renderFormContent() {
    return (
      <div style={{ padding: modalId ? undefined : '0' }}>
        <div>
        {action !== "Add" && action !== "add" && (
          <div className="d-flex justify-content-end mb-3">
            <button
              type="button"
              onClick={() => onTaskDelete && onTaskDelete(updateModalValue?._id)}
              className="btn btn-link text-danger p-0"
              style={{ fontSize: "20px" }}
              title="Delete Task"
            >
              <i className="fa fa-trash-o" />
            </button>
          </div>
        )}

        <div className="form-group mb-3">
          <label className="form_label">Task Type</label>
          <select
            value={taskData?.type}
            onChange={handleChange}
            name="type"
            className="form-select"
          >
            <option value="">Select Task Type</option>
            {taskOptions}
          </select>
          {errors.type && <ErrorText message={errors.type} />}
        </div>
        <div className="form-group mb-3">
          <label className="form_label">Due Date</label>
          <select
            className="form-select"
            value={taskData?.dueDate}
            name="dueDate"
            onChange={(e) => handleSetDueDate(e.target.value)}
          >
            <option value="">Select due date</option>
            {taskDueDates.map((dueDate, idx) => (
              <option key={idx} value={dueDate.value}>{dueDate.name}</option>
            ))}
          </select>
          {errors.toBePerformAt && (
            <ErrorText message={errors.toBePerformAt} />
          )}
        </div>

        {isCustomDate && (
          <div className="form-group mb-3">
            <label className="form_label">Custom Date</label>
            <div className="date-picker-container">
              <DatePicker
                value={taskData.toBePerformAt ? moment(taskData.toBePerformAt) : null}
                onChange={(date) => handleDateChange("toBePerformAt", date)}
                format="YYYY-MM-DD"
                className="w-100"
                disabledDate={(current) => current && current < moment().startOf('day')}
                placeholder="Select date"
              />
            </div>
          </div>
        )}

        <div className="form-group mb-3">
          <label className="form_label">Time</label>
          <div className="timepicker-element">
            <TimePicker
              format="hh:mm A"
              value={taskData.toBePerformAt ? moment(taskData.toBePerformAt) : moment()}
              onChange={(time) => {
                if (time) {
                  Logger.log('Time picked:', time.format());
                  handleDateChange("toBePerformAt", time, "time");
                }
              }}
              placeholder="Select time"
            />
          </div>
          {errors.time && <ErrorText message={errors.time} />}
        </div>
        <div className="form-group mb-3">
          <label className="form_label">Repeat</label>
          <select
            value={taskData?.repeat}
            onChange={handleChange}
            name="repeat"
            className="form-select"
          >
            <option value="NoRepeat">Don't Repeat</option>
            {repeatOptions?.map((option, i) => (
              <option value={option.value} key={i}>
                {option?.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group mb-3">
          <label className="form_label">Assign to</label>
          <select
            name="assignedTo"
            value={taskData?.assignedTo}
            className="form-select"
            onChange={handleChange}
          >
            <option value="">Select Task Assign to</option>
            {taskAssignOptions}
          </select>
        </div>

        <div className="form-group mb-3">
          <label className="form_label">Note</label>
          <textarea
            className="form-control note_area"
            value={taskData?.notes}
            onChange={handleChange}
            name="notes"
            placeholder="Enter your note"
            rows={4}
          />
          {errors.notes && <ErrorText message={errors.notes} />}
        </div>

        {(action === "Edit" || action === "edit") && (
          <div className="form-group mb-3">
            <div className="form-check">
              <input
                type="checkbox"
                checked={isCompleted}
                id="isComplete"
                name="isComplete"
                onChange={() => setIsCompleted(!isCompleted)}
                className="form-check-input"
              />
              <label htmlFor="isComplete" className="form-check-label">
                Is Completed
              </label>
            </div>
          </div>
        )}

        {/* Only show save button when not in modal mode */}
        {!modalId && (
          <div className="d-flex justify-content-end pt-3">
            <button
              type="button"
              onClick={handleTaskSubmit}
              className="btn btn-primary"
              style={{
                backgroundColor: "#6366f1",
                borderColor: "#6366f1",
                padding: "8px 24px",
                fontWeight: "500",
              }}
            >
              SAVE
            </button>
          </div>
        )}
        </div>
      </div>
    );
  }
};

export default CreateLeadDetailsTaskForm;
